import { useEffect, useRef, useState } from 'react'
import { env } from '@/shared/config/env'

export type MapboxSuggestion = {
  mapboxId: string
  name: string
  fullAddress: string
  placeFormatted: string
}

export type AddressSelection = {
  line1: string
  city: string
  state: string
  postalCode: string
  latitude?: number
  longitude?: number
}

type MapboxSuggestApiItem = {
  mapbox_id?: string
  name?: string
  full_address?: string
  place_formatted?: string
}

type MapboxSuggestApiResponse = {
  suggestions?: MapboxSuggestApiItem[]
}

type MapboxRetrieveContext = {
  address?: { street_number?: string; name?: string }
  street?: { name?: string }
  place?: { name?: string }
  region?: { region_code?: string; name?: string }
  postcode?: { name?: string }
}

type MapboxRetrieveFeature = {
  geometry?: { coordinates?: [number, number] }
  properties?: {
    context?: MapboxRetrieveContext
  }
}

type MapboxRetrieveApiResponse = {
  features?: MapboxRetrieveFeature[]
}

const SUGGEST_URL = 'https://api.mapbox.com/search/searchbox/v1/suggest'
const RETRIEVE_URL = 'https://api.mapbox.com/search/searchbox/v1/retrieve'

export function useMapboxAutocomplete() {
  const token = env.mapboxToken
  const [suggestions, setSuggestions] = useState<MapboxSuggestion[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const sessionTokenRef = useRef<string>(crypto.randomUUID())

  const searchPlaces = (input: string) => {
    if (input.length < 3) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      setSuggestions([])
      setIsSearching(false)
      return
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(async () => {
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller
      const { signal } = controller

      setIsSearching(true)

      const params = new URLSearchParams({
        q: input.trim(),
        access_token: token,
        limit: '5',
        language: 'en',
        country: 'us',
        types: 'address,street,place',
        session_token: sessionTokenRef.current,
      })

      try {
        const response = await fetch(`${SUGGEST_URL}?${params.toString()}`, {
          signal,
        })
        if (signal.aborted) return
        if (!response.ok) {
          setIsSearching(false)
          return
        }
        const data = (await response.json()) as MapboxSuggestApiResponse
        if (signal.aborted) return
        const items = data.suggestions ?? []
        setSuggestions(
          items.map((s) => ({
            mapboxId: s.mapbox_id ?? '',
            name: s.name ?? '',
            fullAddress: s.full_address ?? '',
            placeFormatted: s.place_formatted ?? '',
          })),
        )
        setIsSearching(false)
      } catch {
        if (!signal.aborted) {
          setIsSearching(false)
        }
      }
    }, 300)
  }

  const selectSuggestion = async (
    mapboxId: string,
  ): Promise<AddressSelection | null> => {
    const params = new URLSearchParams({
      access_token: token,
      session_token: sessionTokenRef.current,
    })

    try {
      const response = await fetch(
        `${RETRIEVE_URL}/${mapboxId}?${params.toString()}`,
      )
      sessionTokenRef.current = crypto.randomUUID()
      if (!response.ok) {
        clearSuggestions()
        return null
      }
      const data = (await response.json()) as MapboxRetrieveApiResponse
      const features = data.features ?? []
      if (features.length === 0) {
        clearSuggestions()
        return null
      }
      const feature = features[0]
      const ctx = feature.properties?.context ?? {}
      const line1 = [ctx.address?.street_number, ctx.street?.name]
        .filter(Boolean)
        .join(' ')
      const city = ctx.place?.name ?? ''
      const state = (ctx.region?.region_code ?? '').toUpperCase()
      const postalCode = ctx.postcode?.name ?? ''
      const coords = feature.geometry?.coordinates
      const selection: AddressSelection = {
        line1,
        city,
        state,
        postalCode,
      }
      if (Array.isArray(coords) && coords.length >= 2) {
        selection.latitude = coords[1]
        selection.longitude = coords[0]
      }
      clearSuggestions()
      return selection
    } catch {
      clearSuggestions()
      return null
    }
  }

  const clearSuggestions = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    abortControllerRef.current?.abort()
    setSuggestions([])
    setIsSearching(false)
  }

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      abortControllerRef.current?.abort()
    }
  }, [])

  return {
    suggestions,
    isSearching,
    searchPlaces,
    selectSuggestion,
    clearSuggestions,
  }
}
